import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Alert,
  Chip,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  TablePagination,
  TableSortLabel,
  Skeleton,
  Switch,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, VisibilityOff, Refresh, Person, Download, Upload } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { downloadCsvTemplate, USER_TEMPLATE_COLUMNS } from '../utils/csvTemplate';
import ToolbarButton from '../components/ToolbarButton';
import { WaveLoading } from '../components/Loader';

// Loaded on demand: SheetJS is ~150 kB gzipped and only needed for bulk entry.
const BulkGrid = React.lazy(() => import('../components/bulk/BulkGrid'));

// Roles will be fetched from API
const defaultRoles = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'team_member', label: 'Team Member' },
  { value: 'team_leader', label: 'Team Leader' },
  { value: 'view_only', label: 'View Only' },
  { value: 'QC', label: 'QC' },
];

// Admin can assign all roles, project managers and team leaders cannot assign admin role
const getAvailableRoles = (userRole, rolesList) => {
  // If rolesList is empty or not loaded, return default roles
  if (!rolesList || rolesList.length === 0) {
    const defaultRolesList = [
      { value: 'project_manager', label: 'Project Manager' },
      { value: 'team_member', label: 'Team Member' },
      { value: 'team_leader', label: 'Team Leader' },
      { value: 'view_only', label: 'View Only' },
      { value: 'QC', label: 'QC' },
    ];
    if (userRole === 'admin') {
      return [
        { value: 'admin', label: 'Admin' },
        ...defaultRolesList
      ];
    }
    return defaultRolesList;
  }
  
  // Filter roles based on user permissions
  if (userRole === 'admin') {
    // Admin can see all roles including admin
    const adminRole = rolesList.find(r => r.value === 'admin' || r.label === 'Admin');
    const otherRoles = rolesList.filter(r => r.value !== 'admin' && r.label !== 'Admin');
    return adminRole ? [adminRole, ...otherRoles] : rolesList;
  }
  // Non-admin users cannot assign admin, or CMO (which the backend treats as admin)
  const privileged = (r) => ['admin', 'cmo'].includes((r.value || '').toLowerCase()) || ['Admin', 'CMO'].includes(r.label);
  return rolesList.filter(r => !privileged(r));
};

// Get avatar color based on user role
// Notion-style soft pastel palette. We hash the user's name into one of
// these so each person has a consistent personal color (instead of all
// team_members being the same orange).
const AVATAR_PALETTE = [
  { bg: '#fee2e2', fg: '#b91c1c' }, // rose
  { bg: '#ffedd5', fg: '#c2410c' }, // orange
  { bg: '#fef3c7', fg: '#b45309' }, // amber
  { bg: '#dcfce7', fg: '#15803d' }, // green
  { bg: '#cffafe', fg: '#0e7490' }, // cyan
  { bg: '#dbeafe', fg: '#1d4ed8' }, // blue
  { bg: '#ede9fe', fg: '#6d28d9' }, // violet
  { bg: '#fce7f3', fg: '#be185d' }, // pink
];

const getAvatarPalette = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

// Soft, role-keyed pill styles for the Role column.
const ROLE_STYLES = {
  admin:           { bg: '#fee2e2', fg: '#b91c1c' }, // rose
  project_manager: { bg: '#dbeafe', fg: '#1d4ed8' }, // blue
  team_leader:     { bg: '#ede9fe', fg: '#6d28d9' }, // violet
  team_member:     { bg: '#f3f4f6', fg: '#374151' }, // gray
  view_only:       { bg: '#fef3c7', fg: '#b45309' }, // amber
  cmo:             { bg: '#fce7f3', fg: '#be185d' }, // pink
  QC:              { bg: '#cffafe', fg: '#0e7490' }, // cyan
  qc:              { bg: '#cffafe', fg: '#0e7490' },
  partner:         { bg: '#dcfce7', fg: '#15803d' }, // green
};

const getRolePalette = (role) => ROLE_STYLES[role] || { bg: '#f3f4f6', fg: '#374151' };

const formatRoleLabel = (role) =>
  (role || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Employee ID joins users to the biometric attendance DB; must match backend routes/users.js.
const EMPLOYEE_ID_PATTERN = /^[A-Za-z0-9]{1,50}$/;

// Roles that can be someone's "Reports to" (timesheet approver); must match backend routes/users.js.
const APPROVER_ROLES = ['admin', 'project_manager', 'team_leader', 'cmo'];
const roleLabel = (role) => (role || '').replace(/_/g, ' ');
// Managers grouped by role in the pickers: project managers first, then team leaders, CMOs, admins.
const MANAGER_ORDER = ['project_manager', 'team_leader', 'cmo', 'admin'];
const byRoleThenName = (a, b) =>
  (MANAGER_ORDER.indexOf(a.role) - MANAGER_ORDER.indexOf(b.role)) || (a.name || '').localeCompare(b.name || '');

const UserManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { canPerformActionByRoute, moduleIdMap, loading: permissionsLoading } = usePermissions();
  const usersModuleId = moduleIdMap['Users'];
  
  // Permission checks
  const canAdd = user?.role === 'admin' || canPerformActionByRoute('/users', 'Add');
  const canEdit = user?.role === 'admin' || canPerformActionByRoute('/users', 'Edit');
  const canDelete = user?.role === 'admin' || canPerformActionByRoute('/users', 'Delete');
  const canView = user?.role === 'admin' || canPerformActionByRoute('/users', 'View');
  
  const canModify = canAdd || canEdit || canDelete;
  
  // Field visibility state
  const [hiddenFields, setHiddenFields] = useState([]);
  
  // Fetch field visibility for Users module
  useEffect(() => {
    const fetchFieldVisibility = async () => {
      if (!user?.serviceid || !usersModuleId) {
        setHiddenFields([]);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/service-management/field-visibility/${user.serviceid}/${usersModuleId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setHiddenFields(data.hiddenFields || []);
        } else {
          setHiddenFields([]);
        }
      } catch (error) {
        console.error('Error fetching field visibility:', error);
        setHiddenFields([]);
      }
    };
    
    fetchFieldVisibility();
  }, [user?.serviceid, usersModuleId]);
  
  // Helper to check if a field is hidden
  const isFieldHidden = (fieldName) => {
    // For PM role: always hide service field (it will be auto-populated)
    if (fieldName === 'serviceid' && user?.role === 'project_manager') {
      return true;
    }
    return hiddenFields.includes(fieldName);
  };
  
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');
  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: 'team_member',
    roleid: null,
    serviceid: '',
    employeeId: '',
    reportsTo: null,
    changePassword: false
  });
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, totalPages: 0 });
  const [abortController, setAbortController] = useState(null);
  
  // Add new role dialog state
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleServiceId, setNewRoleServiceId] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);

  // "Assign managers" tool: suggested Reports to for everyone who has none.
  const [reportsToOpen, setReportsToOpen] = useState(false);
  const [reportsToRows, setReportsToRows] = useState([]);
  const [reportsToManagers, setReportsToManagers] = useState([]);
  const [loadingReportsTo, setLoadingReportsTo] = useState(false);
  const [applyingReportsTo, setApplyingReportsTo] = useState(false);

  const openReportsTo = async () => {
    setReportsToOpen(true);
    setLoadingReportsTo(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/reports-to/suggestions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Failed to load suggestions');
      const data = await res.json();
      setReportsToManagers((data.managers || []).slice().sort(byRoleThenName));
      setReportsToRows((data.people || []).map((p) => ({ ...p, choice: p.suggestedId, include: !!p.suggestedId })));
    } catch (err) {
      setError(err.message);
      setReportsToOpen(false);
    } finally {
      setLoadingReportsTo(false);
    }
  };

  const applyReportsTo = async () => {
    const assignments = reportsToRows.filter((r) => r.include && r.choice).map((r) => ({ userId: r.id, reportsTo: r.choice }));
    if (!assignments.length) return;
    setApplyingReportsTo(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/reports-to/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ assignments }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save');
      const failedIds = new Set((data.failed || []).map((f) => f.userId));
      setReportsToRows((rows) => rows
        .filter((r) => !(data.updated || []).includes(r.id))
        .map((r) => (failedIds.has(r.id) ? { ...r, error: data.failed.find((f) => f.userId === r.id)?.error } : r)));
      setSuccess(`Reports to set for ${data.updated.length} user${data.updated.length === 1 ? '' : 's'}${data.failed?.length ? `, ${data.failed.length} could not be saved` : ''}.`);
      setTimeout(() => setSuccess(''), 4000);
      if (!data.failed?.length) setReportsToOpen(false);
      fetchUsers(1, 10000);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingReportsTo(false);
    }
  };
  const [bulkGridOpen, setBulkGridOpen] = useState(false);

  const fetchUsers = async (pageNum = 1, limit = 10000) => {
    try {
      setLoadingUsers(true);
      setError(''); // Clear previous errors
      
      // Cancel any existing request
      if (abortController) {
        abortController.abort();
      }
      
      // Input validation
      const validPage = Math.max(1, parseInt(pageNum) || 1);
      const validLimit = Math.max(1, parseInt(limit) || 5);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Add timeout handling and request cancellation
      const controller = new AbortController();
      setAbortController(controller);
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users?page=${validPage}&limit=${validLimit}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error:', errorText);
        throw new Error(`Failed to fetch users: ${res.status} ${errorText}`);
      }
      
      const data = await res.json();
      
      // Handle empty response
      if (!data) {
        setUsers([]);
        setPagination({ total: 0, page: 1, limit: validLimit, totalPages: 0 });
        return;
      }
      
      setUsers(Array.isArray(data) ? data : data.data || []);
      if (data.pagination) {
        setPagination(data.pagination);
      } else {
        // Fallback pagination for non-paginated responses
        const users = Array.isArray(data) ? data : data.data || [];
        setPagination({ 
          total: users.length, 
          page: validPage, 
          limit: validLimit, 
          totalPages: Math.ceil(users.length / validLimit) 
        });
      }
      console.log('Fetched users:', data);
    } catch (err) {
      console.error('Fetch users error:', err);
      
      // Don't show error for cancelled requests
      if (err.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      
      // Handle different types of errors
      let errorMessage = 'Failed to fetch users';
      if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.message.includes('401')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.message.includes('403')) {
        errorMessage = 'Access denied. You do not have permission.';
      } else if (err.message.includes('404')) {
        errorMessage = 'Users endpoint not found.';
      } else if (err.message.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = `Failed to fetch users: ${err.message}`;
      }
      
      setError(errorMessage);
      // Set empty state on error
      setUsers([]);
      setPagination({ total: 0, page: 1, limit: 5, totalPages: 0 });
    } finally {
      setLoadingUsers(false);
      setAbortController(null);
    }
  };

  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/service-lookup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch services');
      }
      
      const data = await res.json();
      setServices(data);
    } catch (err) {
      console.error('Fetch services error:', err);
      setError('Failed to fetch services');
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchRoles = async (serviceId = null) => {
    try {
      setLoadingRoles(true);
      const token = localStorage.getItem('token');
      
      // Build URL with optional serviceId parameter
      let url = `${process.env.REACT_APP_API_URL}/service-management/roles`;
      if (serviceId && serviceId !== '' && serviceId !== 'null') {
        url += `?serviceId=${encodeURIComponent(serviceId)}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch roles');
      }
      
      const data = await res.json();
      // Map roles from API to format expected by dropdown
      const mappedRoles = data.map(role => {
        // Map role name to expected format
        let roleValue = role.RolesName;
        // Convert common role names to expected format
        const roleMapping = {
          'Admin': 'admin',
          'Project Manager': 'project_manager',
          'Team Member': 'team_member',
          'Team Leader': 'team_leader',
          'View Only': 'view_only',
          'QC': 'QC',
          'qc': 'QC',
          'Art': 'art',
          'Copy': 'copy',
          'Research': 'research',
        };
        
        // Use mapping if available, otherwise convert to lowercase with underscores
        if (roleMapping[role.RolesName]) {
          roleValue = roleMapping[role.RolesName];
        } else {
          roleValue = role.RolesName.toLowerCase().replace(/\s+/g, '_');
        }
        
        return {
          id: role.ID,
          value: roleValue,
          label: role.RolesName,
          roleid: role.ID
        };
      });
      setRoles(mappedRoles);
    } catch (err) {
      console.error('Fetch roles error:', err);
      // Fallback to default roles if API fails
      setRoles(defaultRoles.map(r => ({ ...r, id: null, roleid: null })));
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchUsers(1, 10000);
    fetchServices();
    // Fetch roles based on logged-in user's serviceid (or all roles if admin/no serviceid)
    fetchRoles(user?.serviceid || null);
    
    // Cleanup function to cancel any pending requests
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [user?.serviceid]);

  const handleOpen = (userToEdit = null) => {
    setError('');
    setSuccess('');
    if (userToEdit) {
      // For PM role: auto-populate serviceid from logged-in user's service
      const serviceId = user?.role === 'project_manager' ? (user.serviceid || '') : (userToEdit.serviceid || '');
      setForm({ 
        name: userToEdit.name, 
        email: userToEdit.email, 
        password: '', 
        role: userToEdit.role,
        roleid: userToEdit.roleid || null,
        serviceid: serviceId,
        employeeId: userToEdit.employeeId || '',
        reportsTo: userToEdit.reportsTo || null,
        changePassword: false
      });
      setEditId(userToEdit.id);
      setOriginalEmail(userToEdit.email);
      // Fetch roles based on logged-in user's serviceid (not the form's serviceid)
      // This ensures users only see roles available to their service
      fetchRoles(user?.serviceid || null);
    } else {
      // For PM role: auto-populate serviceid from logged-in user's service
      const serviceId = user?.role === 'project_manager' ? (user.serviceid || '') : '';
      setForm({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'team_member',
        roleid: null,
        serviceid: serviceId,
        employeeId: '',
        reportsTo: null,
        changePassword: false
      });
      setEditId(null);
      setOriginalEmail('');
      // Fetch roles based on logged-in user's serviceid (not the form's serviceid)
      // This ensures users only see roles available to their service
      fetchRoles(user?.serviceid || null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ 
      name: '', 
      email: '', 
      password: '', 
      role: 'team_member',
      roleid: null,
      serviceid: '',
      employeeId: '',
      reportsTo: null,
      changePassword: false
    });
    setEditId(null);
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updatedValue = type === 'checkbox' ? checked : value;
    let updatedForm;
    
    // If serviceid is changed, update form but don't refetch roles
    // Roles are always filtered by logged-in user's serviceid, not the form's serviceid
    if (name === 'serviceid') {
      updatedForm = { 
        ...form, 
        [name]: updatedValue 
      };
      setForm(updatedForm);
      // Note: We don't refetch roles here because roles should always be filtered
      // by the logged-in user's serviceid, not the form's serviceid
      return;
    }
    
    // If role is changed, find and set the corresponding roleid and ensure correct case
    if (name === 'role') {
      if (roles.length > 0) {
        // Find role by value (case-insensitive) or by label
        const selectedRole = roles.find(r => 
          r.value?.toLowerCase() === value?.toLowerCase() || 
          r.label?.toLowerCase() === value?.toLowerCase() ||
          r.value === value ||
          r.label === value
        );
        
        if (selectedRole) {
          // Use the correct case from the roles array
          updatedValue = selectedRole.value;
          if (selectedRole.roleid) {
            updatedForm = { 
              ...form, 
              [name]: updatedValue,
              roleid: selectedRole.roleid
            };
          } else {
            updatedForm = { 
              ...form, 
              [name]: updatedValue,
              roleid: null
            };
          }
        } else {
          // If role not found, normalize common role names to correct case
          const roleCaseMap = {
            'qc': 'QC',
            'admin': 'admin',
            'project_manager': 'project_manager',
            'team_member': 'team_member',
            'team_leader': 'team_leader',
            'view_only': 'view_only',
            'art': 'art',
            'copy': 'copy',
            'research': 'research',
          };
          updatedValue = roleCaseMap[value?.toLowerCase()] || value;
          updatedForm = { 
            ...form, 
            [name]: updatedValue,
            roleid: null
          };
        }
      } else {
        // If roles not loaded, normalize common role names
        const roleCaseMap = {
          'qc': 'QC',
          'admin': 'admin',
          'project_manager': 'project_manager',
          'team_member': 'team_member',
          'team_leader': 'team_leader',
          'view_only': 'view_only',
          'art': 'art',
          'copy': 'copy',
          'research': 'research',
        };
        updatedValue = roleCaseMap[value?.toLowerCase()] || value;
        updatedForm = { 
          ...form, 
          [name]: updatedValue
        };
      }
    } else {
      updatedForm = { 
        ...form, 
        [name]: updatedValue 
      };
    }
    
    setForm(updatedForm);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    
    // Validation - skip hidden fields
    if (!isFieldHidden('name') && !form.name) {
      setError('Name is required');
      return;
    }
    if (!isFieldHidden('email') && !form.email) {
      setError('Email is required');
      return;
    }
    // Employee ID joins to the biometric attendance DB: required for new users,
    // optional when editing older users who don't have one yet.
    const employeeId = (form.employeeId || '').trim();
    if (!editId && !employeeId) {
      setError('Employee ID is required');
      return;
    }
    if (employeeId && !EMPLOYEE_ID_PATTERN.test(employeeId)) {
      setError('Employee ID must be letters and numbers only');
      return;
    }
    if (!isFieldHidden('role') && !form.role) {
      setError('Role is required');
      return;
    }
    // For PM role: serviceid is auto-populated, so skip validation
    // For admin role: service is optional (empty = global admin, set = scoped admin)
    // For other roles: validate serviceid if field is visible
    const isAdminRoleSubmit = (form.role || '').toLowerCase() === 'admin';
    if (!isFieldHidden('serviceid') && !form.serviceid && !isAdminRoleSubmit) {
      setError('Service is required');
      return;
    }
    // Ensure PM has serviceid set (auto-populated)
    if (user?.role === 'project_manager' && !form.serviceid) {
      setError('Service is required');
      return;
    }
    
    if (!isFieldHidden('password') && !editId && !form.password) {
      setError('Password is required for new users');
      return;
    }
    
    if (!isFieldHidden('password') && editId && form.changePassword && !form.password) {
      setError('Password is required when changing password');
      return;
    }

    // Check if email is being changed by admin
    if (editId && user.role === 'admin' && form.email !== originalEmail) {
      setEmailChangeOpen(true);
      return;
    }

    await performUserUpdate();
  };

  const performUserUpdate = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Normalize role to ensure correct case
      const roleCaseMap = {
        'qc': 'QC',
        'admin': 'admin',
        'project_manager': 'project_manager',
        'team_member': 'team_member',
        'team_leader': 'team_leader',
        'view_only': 'view_only',
        'art': 'art',
        'copy': 'copy',
        'research': 'research',
      };
      const normalizedRole = roleCaseMap[form.role?.toLowerCase()] || form.role;
      
      // Check if email is being changed by admin
      if (editId && user.role === 'admin' && form.email !== originalEmail) {
        // Use the email-specific endpoint for email changes
        const emailRes = await fetch(`${process.env.REACT_APP_API_URL}/users/${editId}/email`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: form.email }),
        });
        
        if (!emailRes.ok) {
          const data = await emailRes.json();
          throw new Error(data.message || 'Failed to update email');
        }
        
        // Update other fields using regular endpoint
        const method = 'PUT';
        const url = `${process.env.REACT_APP_API_URL}/users/${editId}`;
        
        const requestBody = {};
        
        // Only include non-hidden fields
        if (!isFieldHidden('name')) {
          requestBody.name = form.name;
        }
        if (!isFieldHidden('role')) {
          requestBody.role = normalizedRole;
        }
        requestBody.employeeId = (form.employeeId || '').trim();
        requestBody.reportsTo = form.reportsTo || null;
        // For PM role: always include serviceid (auto-populated, field is hidden)
        // For other roles: include serviceid if field is visible.
        // For admin role: serviceid may be empty (global admin) — send null instead of NaN.
        if (user?.role === 'project_manager') {
          requestBody.serviceid = parseInt(form.serviceid || user.serviceid);
        } else if (!isFieldHidden('serviceid')) {
          requestBody.serviceid = form.serviceid ? parseInt(form.serviceid) : null;
        }

        // Add roleid if available
        if (form.roleid) {
          requestBody.roleid = form.roleid;
        }

        // Only include password if changing password and not hidden
        if (!isFieldHidden('password') && form.changePassword && form.password) {
          requestBody.password = form.password;
        }
        
        const res = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
        
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || 'Failed to update user');
        }
        
        setSuccess('User updated successfully! Email change will require re-authentication.');
      } else {
        // Regular update flow
        const method = editId ? 'PUT' : 'POST';
        const url = `${process.env.REACT_APP_API_URL}/users${editId ? `/${editId}` : ''}`;
        
        // Normalize role to ensure correct case
        const roleCaseMap = {
          'qc': 'QC',
          'admin': 'admin',
          'project_manager': 'project_manager',
          'team_member': 'team_member',
          'team_leader': 'team_leader',
          'view_only': 'view_only',
        };
        const normalizedRole = roleCaseMap[form.role?.toLowerCase()] || form.role;
        
        const requestBody = {};
        
        // Only include non-hidden fields
        if (!isFieldHidden('name')) {
          requestBody.name = form.name;
        }
        if (!isFieldHidden('email')) {
          requestBody.email = form.email;
        }
        if (!isFieldHidden('role')) {
          requestBody.role = normalizedRole;
        }
        requestBody.employeeId = (form.employeeId || '').trim();
        requestBody.reportsTo = form.reportsTo || null;
        // For PM role: always include serviceid (auto-populated, field is hidden)
        // For other roles: include serviceid if field is visible.
        // For admin role: serviceid may be empty (global admin) — send null instead of NaN.
        if (user?.role === 'project_manager') {
          requestBody.serviceid = parseInt(form.serviceid || user.serviceid);
        } else if (!isFieldHidden('serviceid')) {
          requestBody.serviceid = form.serviceid ? parseInt(form.serviceid) : null;
        }

        // Add roleid if available
        if (form.roleid) {
          requestBody.roleid = form.roleid;
        }

        // Only include password if it's a new user or if changing password for existing user, and not hidden
        if (!isFieldHidden('password') && (!editId || (editId && form.changePassword && form.password))) {
          requestBody.password = form.password;
        }
        
        const res = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
        
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || 'Failed to save user');
        }
        
        setSuccess(editId ? 'User updated successfully!' : 'User created successfully!');
      }
      
      setTimeout(() => {
        handleClose();
        fetchUsers(1, 10000);
      }, 1500);
    } catch (err) {
      setError(err.message);
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (userToDelete) => {
    setUserToDelete(userToDelete);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to delete user');
      }

      setSuccess('User deleted successfully!');
      setDeleteOpen(false);
      setUserToDelete(null);
      fetchUsers(1, 10000);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      console.error('Delete error:', err);
      setDeleteOpen(false);
      setUserToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setUserToDelete(null);
  };

  const handleEmailChangeConfirm = async () => {
    setChangingEmail(true);
    setEmailChangeOpen(false);
    await performUserUpdate();
    setChangingEmail(false);
  };

  const handleEmailChangeCancel = () => {
    setEmailChangeOpen(false);
    // Reset email to original
    setForm({ ...form, email: originalEmail });
  };

  // ---- Bulk grid (paste / import / type) --------------------------------------
  // Project managers and team leaders add people to their own service only, so
  // the Service column is dropped for them and filled in automatically.
  const bulkOwnService = ['project_manager', 'team_leader'].includes(user?.role);
  const bulkColumns = useMemo(() => {
    const roleOptions = getAvailableRoles(user?.role, roles).map((r) => ({ value: String(r.value).toLowerCase() === 'qc' ? 'QC' : r.value, label: r.label }));
    const managerEmails = users.filter((u) => APPROVER_ROLES.includes(u.role) && u.isActive !== false).map((u) => u.email).filter(Boolean).sort();
    return [
      { key: 'name', label: 'Name', type: 'text', required: true, width: 180, aliases: ['full name', 'employee name', 'user'] },
      { key: 'email', label: 'Email', type: 'email', required: true, width: 230, aliases: ['email address', 'email id', 'mail id', 'mail', 'user email', 'official email'] },
      { key: 'password', label: 'Password', type: 'text', required: true, width: 140, aliases: ['pwd', 'initial password'], help: 'Initial password; they can change it after signing in.' },
      { key: 'employeeId', label: 'Employee ID', type: 'text', required: true, width: 120, aliases: ['emp id', 'emp no', 'employee number', 'employee code', 'emp code', 'id'], help: 'Letters and numbers only; must match the biometric system.' },
      { key: 'role', label: 'Role', type: 'select', required: true, width: 160, options: roleOptions, aliases: ['designation', 'user role', 'position', 'access'],
        synonyms: { pm: 'project_manager', 'proj manager': 'project_manager', manager: 'project_manager', tl: 'team_leader', 'team lead': 'team_leader', lead: 'team_leader', leader: 'team_leader', member: 'team_member', 'team member': 'team_member', tm: 'team_member', employee: 'team_member', staff: 'team_member', associate: 'team_member', viewer: 'view_only', 'read only': 'view_only', 'view only': 'view_only', qc: 'QC', 'quality check': 'QC', 'quality control': 'QC' } },
      { key: 'serviceName', label: 'Service', type: 'lookup', width: 180, lookupOptions: services.map((sv) => sv.Service).filter(Boolean), aliases: ['department', 'team', 'service name'], help: 'Required for everyone except admins.' },
      { key: 'reportsToEmail', label: 'Reports to (email)', type: 'lookup', width: 230, lookupOptions: managerEmails, emails: true, aliases: ['manager', 'manager email', 'reports to', 'reporting manager', 'approver', 'supervisor'], help: 'The manager who approves their timesheets.' },
    ].filter((c) => !(bulkOwnService && c.key === 'serviceName'));
  }, [user?.role, roles, users, services, bulkOwnService]);

  const validateUserRow = useCallback((v) => {
    const errors = {};
    const lc = (x) => String(x || '').trim().toLowerCase();
    const employeeId = String(v.employeeId || '').trim();
    if (employeeId && !EMPLOYEE_ID_PATTERN.test(employeeId)) errors.employeeId = 'Letters and numbers only';
    const roleValue = lc(v.role) === 'qc' ? 'QC' : lc(v.role);
    const isAdminRow = roleValue === 'admin';
    let serviceId = null;
    if (bulkOwnService) {
      serviceId = user?.serviceid || null; // PM / TL: always their own service
    } else if (v.serviceName) {
      const svc = services.find((sv) => lc(sv.Service) === lc(v.serviceName));
      if (!svc) errors.serviceName = `No service called "${v.serviceName}"`;
      else serviceId = svc.Id;
    } else if (v.role && !isAdminRow) {
      errors.serviceName = 'Service is required (optional only for admins)';
    }
    if (v.reportsToEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.reportsToEmail.trim())) errors.reportsToEmail = 'Not an email address';
    return {
      errors,
      payload: {
        name: String(v.name || '').trim(),
        email: String(v.email || '').trim(),
        password: v.password,
        employeeId,
        role: roleValue,
        serviceid: serviceId,
        reportsToEmail: (v.reportsToEmail || '').trim() || undefined,
      },
    };
  }, [services, bulkOwnService, user?.serviceid]);

  const userDedupeKey = useCallback((v) => (v.email ? v.email.trim().toLowerCase() : null), []);

  const postBulkUsers = async (payloads, dryRun) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ users: payloads, dryRun }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Bulk upload failed');
    return data;
  };
  const bulkDryRun = (payloads) => postBulkUsers(payloads, true);
  const bulkSubmit = async (payloads) => {
    const data = await postBulkUsers(payloads, false);
    fetchUsers(1, 10000);
    return data;
  };

  const handleToggleStatus = async (userToToggle) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${userToToggle.id}/toggle-status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to update user status');
      }
      
      const data = await res.json();
      const toggledStatus = userToToggle.isActive === false ? 'activated' : 'deactivated';
      setSuccess(`${userToToggle.name} was ${toggledStatus}.`);
      fetchUsers(1, 10000); // Refresh the users list
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      console.error('Toggle status error:', err);
    }
  };


  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'project_manager': return 'primary';
      case 'team_member': return 'default';
      case 'team_leader': return 'info';
      case 'view_only': return 'warning';
      case 'QC':
      case 'qc': return 'info';
      default: return 'default';
    }
  };

  const canDeleteUser = (userToCheck) => {
    // Users cannot delete themselves
    if (user.role === 'view_only') return false;
    if (userToCheck.id === user.id) {
      return false;
    }
    
    // Project managers and team leaders cannot delete admin users
    if ((user.role === 'project_manager' || user.role === 'team_leader') && userToCheck.role === 'admin') {
      return false;
    }
    
    // Admins cannot delete other admins
    if (user.role === 'admin' && userToCheck.role === 'admin') {
      return false;
    }
    
    return true;
  };

  const canEditUser = (userToCheck) => {
    // Project managers and team leaders cannot edit admin users
    if (user.role === 'view_only') return false;
    if ((user.role === 'project_manager' || user.role === 'team_leader') && userToCheck.role === 'admin') {
      return false;
    }
    
    return true;
  };

  const canToggleUserStatus = (userToCheck) => {
    // Users cannot toggle their own status
    if (user.role === 'view_only') return false;
    if (userToCheck.id === user.id) {
      return false;
    }
    
    // Project managers and team leaders cannot toggle admin users
    if ((user.role === 'project_manager' || user.role === 'team_leader') && userToCheck.role === 'admin') {
      return false;
    }
    
    return true;
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPage(0);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setPage(0);
  };

  // Export the currently filtered users (search + role filter applied) to CSV
  const handleExportUsers = () => {
    if (!filteredUsers.length) {
      setError('No users to export');
      return;
    }

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (/[",\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Map serviceid -> service name for a readable column
    const serviceNameById = new Map(
      (services || []).map((s) => [s.Id, s.Service])
    );

    const headers = ['Name', 'Email', 'Employee ID', 'Role', 'Service', 'Reports To', 'Status'];
    const rows = filteredUsers.map((u) => [
      escapeCsvValue(u.name),
      escapeCsvValue(u.email),
      escapeCsvValue(u.employeeId || ''),
      escapeCsvValue((u.role || '').replace(/_/g, ' ')),
      escapeCsvValue(serviceNameById.get(u.serviceid) || ''),
      escapeCsvValue(u.manager?.email || ''),
      escapeCsvValue(u.isActive !== false ? 'Active' : 'Inactive'),
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCloseAddRoleDialog = () => {
    setAddRoleDialogOpen(false);
    setNewRoleName('');
    setNewRoleServiceId('');
    setError('');
  };

  const handleSaveNewRole = async () => {
    if (!newRoleName.trim()) {
      setError('Role name is required.');
      return;
    }

    try {
      setSavingRole(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const requestBody = { RolesName: newRoleName.trim() };
      // Include serviceid if selected (optional - null/empty means global role)
      if (newRoleServiceId && newRoleServiceId !== '') {
        requestBody.serviceid = parseInt(newRoleServiceId);
      }
      
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/roles`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create role');
      }

      setSuccess('Role created successfully!');
      setAddRoleDialogOpen(false);
      setNewRoleName('');
      setNewRoleServiceId('');
      // Refresh roles list
      fetchRoles(user?.serviceid || null);
    } catch (err) {
      console.error('Create role error:', err);
      setError(err.message || 'Failed to create role. Please try again.');
    } finally {
      setSavingRole(false);
    }
  };

  const handleSort = (prop) => {
    const isAsc = orderBy === prop && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(prop);
  };

  const handleChangePage = (_, newPage) => {
    // Boundary check: prevent negative pages
    if (newPage < 0) return;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    // Boundary check: ensure valid rows per page
    if (isNaN(newRowsPerPage) || newRowsPerPage <= 0) return;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  function descendingComparator(a, b, prop) {
    if (b[prop] < a[prop]) return -1;
    if (b[prop] > a[prop]) return 1;
    return 0;
  }

  function getComparator(ord, prop) {
    return ord === 'desc' ? (a, b) => descendingComparator(a, b, prop) : (a, b) => -descendingComparator(a, b, prop);
  }

  function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const orderRes = comparator(a[0], b[0]);
      if (orderRes !== 0) return orderRes;
      return a[1] - b[1];
    });
    return stabilized.map(el => el[0]);
  }

  // Distinct roles present in the loaded users (for the Role filter dropdown)
  const availableRoleFilters = [...new Set(users.map((u) => u.role).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

  // Client-side filtering by role then search text (name, email, role, status)
  const filteredUsers = users.filter((u) => {
    // Role filter (exact match on stored role value)
    if (roleFilter && u.role?.toLowerCase() !== roleFilter.toLowerCase()) {
      return false;
    }
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    const statusText = (u.isActive !== false ? 'active' : 'inactive');
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.employeeId?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q) ||
      statusText.includes(q)
    );
  });

  // Client-side pagination on filtered results
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedFilteredUsers = filteredUsers.slice(startIndex, endIndex);

  return (
    <Box 
      p={3}
      sx={{
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
        '& *': {
          fontFamily: 'inherit',
        }
      }}
    >
      <Typography 
        variant="h5" 
        mb={2}
        sx={{
          fontWeight: 600,
          fontSize: '1.5rem',
          lineHeight: 1.4,
          letterSpacing: '-0.02em',
          color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
        }}
      >
        User Management
      </Typography>
      {/* Filter Section — Notion-style soft bar */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: 2.5,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search Users"
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              width: 200,
              '& .MuiInputBase-input': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                fontWeight: 400,
                letterSpacing: '0.01em',
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
              }
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="role-filter-label">Filter by Role</InputLabel>
            <Select
              labelId="role-filter-label"
              label="Filter by Role"
              value={roleFilter}
              onChange={handleRoleFilterChange}
              sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}
            >
              <MenuItem value="">All Roles</MenuItem>
              {availableRoleFilters.map((r) => (
                <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                  {r.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>
      </Box>

      {/* Action button row — separate from filter container for consistency with other pages */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        {canAdd && (
          <ToolbarButton
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Add User
          </ToolbarButton>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
          <ToolbarButton
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchUsers(1, 10000)}
            sx={{
              color: 'text.secondary',
              borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
              '&:hover': {
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
              },
            }}
          >
            Refresh
          </ToolbarButton>
          {user?.role === 'admin' && (
            <ToolbarButton
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportUsers}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Export Users
            </ToolbarButton>
          )}
          {user?.role === 'admin' && (
            <ToolbarButton
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddRoleDialogOpen(true)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Add New Role
            </ToolbarButton>
          )}
          {user?.role === 'admin' && (
            <ToolbarButton
              variant="outlined"
              startIcon={<Person />}
              onClick={openReportsTo}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Assign Managers
            </ToolbarButton>
          )}
          {canAdd && (
            <ToolbarButton
              variant="outlined"
              startIcon={<Download />}
              onClick={() => downloadCsvTemplate('users_template.csv', USER_TEMPLATE_COLUMNS)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Download Template
            </ToolbarButton>
          )}
          {canAdd && ['admin', 'project_manager', 'team_leader'].includes(user?.role) && (
            <ToolbarButton
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setBulkGridOpen(true)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Add Many Users
            </ToolbarButton>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      {permissionsLoading ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <WaveLoading theme={theme} size="large" />
        </Box>
      ) : !canView ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography 
            variant="h6" 
            color="textSecondary"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '1.125rem',
              lineHeight: 1.5,
              letterSpacing: '0.01em',
            }}
          >
            You don't have permission to view this data.
          </Typography>
        </Box>
      ) : (
      <TableContainer
        component={Paper}
        sx={{
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.3)' 
              : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.5)' 
                : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              <TableCell 
                sortDirection={orderBy === 'name' ? order : false} 
                sx={{ 
                  color: 'white', 
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  letterSpacing: '0.02em',
                }}
              >
                <TableSortLabel 
                  active={orderBy === 'name'} 
                  direction={orderBy === 'name' ? order : 'asc'} 
                  onClick={() => handleSort('name')} 
                  sx={{ 
                    color: 'white', 
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' } 
                  }}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
              }}>
                Email
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
              }}>
                Role
              </TableCell>
              <TableCell sx={{
                color: 'white',
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
              }}>
                Reports to
              </TableCell>
              <TableCell sx={{
                color: 'white',
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
              }}>
                Status
              </TableCell>
              {canModify && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  letterSpacing: '0.02em',
                }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingUsers ? (
              <TableRow>
                <TableCell
                  colSpan={canModify ? 6 : 5}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <WaveLoading theme={theme} size="large" />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500,
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '1.125rem',
                        lineHeight: 1.5,
                        letterSpacing: '0.01em',
                      }}
                    >
                      Loading users...
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        letterSpacing: '0.01em',
                      }}
                    >
                      Please wait while we fetch the latest user data
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : stableSort(paginatedFilteredUsers, getComparator(order, orderBy))
                  .map((u) => (
              <TableRow
                key={u.id}
                hover
                sx={{
                  borderBottom: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                  }
                }}
              >
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  lineHeight: 1.6,
                  letterSpacing: '0.01em',
                }}>
                  {(() => {
                    const pal = getAvatarPalette(u.name);
                    return (
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            bgcolor: pal.bg,
                            color: pal.fg,
                            width: 32,
                            height: 32,
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            boxShadow: 'none',
                            border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.04)',
                          }}
                        >
                          {getInitials(u.name)}
                        </Avatar>
                        <Box>
                          <span style={{
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            letterSpacing: '0.01em',
                          }}>{u.name}</span>
                          {u.employeeId && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              ID: {u.employeeId}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  lineHeight: 1.6,
                  letterSpacing: '0.01em',
                }}>{u.email}</TableCell>
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                }}>
                  {(() => {
                    const pal = getRolePalette(u.role);
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1,
                          py: 0.25,
                          bgcolor: pal.bg,
                          color: pal.fg,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: 1,
                          whiteSpace: 'nowrap',
                          letterSpacing: 0,
                        }}
                      >
                        {formatRoleLabel(u.role)}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                }}>
                  {u.manager?.name
                    ? u.manager.name
                    : APPROVER_ROLES.includes(u.role) || !u.isActive
                      ? <span style={{ opacity: 0.5 }}>—</span>
                      : <span style={{ color: theme.palette.warning.main, fontSize: '0.8125rem' }}>Not set</span>}
                </TableCell>
                <TableCell sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                }}>
                  {(() => {
                    const active = u.isActive !== false;
                    const canToggle = canToggleUserStatus(u);
                    const palette = active
                      ? { bg: '#dcfce7', fg: '#15803d', dot: '#10b981' }
                      : { bg: '#f3f4f6', fg: '#6b7280', dot: '#9ca3af' };
                    const pill = (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1,
                          py: 0.4,
                          bgcolor: palette.bg,
                          color: palette.fg,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: 1,
                          letterSpacing: 0,
                        }}
                      >
                        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.dot, flexShrink: 0 }} />
                        {active ? 'Active' : 'Inactive'}
                      </Box>
                    );
                    if (!canToggle) return pill;
                    return (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
                        <Box
                          role="switch"
                          aria-checked={active}
                          tabIndex={0}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleStatus(u); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleToggleStatus(u);
                            }
                          }}
                          sx={{
                            position: 'relative',
                            width: 26,
                            height: 15,
                            borderRadius: 8,
                            bgcolor: active ? '#10b981' : '#d1d5db',
                            cursor: 'pointer',
                            transition: 'background-color 0.18s ease',
                            flexShrink: 0,
                            '&:hover': { filter: 'brightness(0.95)' },
                            '&:focus-visible': {
                              outline: '2px solid #3b82f6',
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              left: active ? 13 : 2,
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              bgcolor: '#ffffff',
                              transition: 'left 0.18s ease',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                            }}
                          />
                        </Box>
                        {pill}
                      </Box>
                    );
                  })()}
                </TableCell>
                {canModify && (
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400,
                    fontFamily: '"Inter", sans-serif',
                  }}>
                    {canEdit && canEditUser(u) && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleOpen(u)}
                          size="small"
                          sx={{
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                            }
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Delete button hidden for all users */}
                  </TableCell>
                )}
              </TableRow>
                  ))
            }
            {!loadingUsers && filteredUsers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={canModify ? 6 : 5}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500,
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '1.125rem',
                        lineHeight: 1.5,
                        letterSpacing: '0.01em',
                      }}
                    >
                      No users found
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        letterSpacing: '0.01em',
                      }}
                    >
                      Try adjusting your search or add a new user
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={Math.max(0, filteredUsers.length)}
          page={Math.max(0, Math.min(page, Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1)))}
          onPageChange={handleChangePage}
          rowsPerPage={Math.max(1, Math.min(100, rowsPerPage))}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          showFirstButton
          showLastButton
          sx={{
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-input': {
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.875rem',
              fontWeight: 400,
              letterSpacing: '0.01em',
            },
            '& .MuiIconButton-root': {
              fontFamily: '"Inter", sans-serif',
            }
          }}
        />
      </TableContainer>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClose();
          }
        }}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            fontFamily: '"Inter", sans-serif',
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          {editId ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          {!isFieldHidden('name') && (
            <TextField
              margin="normal"
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                },
              }}
            />
          )}
          {!isFieldHidden('email') && (
            <TextField
              margin="normal"
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              disabled={!!editId && user.role !== 'admin'}
              helperText={editId && user.role === 'admin' ? 'Changing email will require user to re-authenticate' : ''}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.8125rem',
                },
              }}
            />
          )}
          {(() => {
            const employeeIdInvalid = !!form.employeeId && !EMPLOYEE_ID_PATTERN.test(form.employeeId.trim());
            return (
              <TextField
                margin="normal"
                label="Employee ID"
                name="employeeId"
                value={form.employeeId}
                onChange={handleChange}
                fullWidth
                required={!editId}
                error={employeeIdInvalid}
                helperText={
                  employeeIdInvalid
                    ? 'Letters and numbers only'
                    : editId && !form.employeeId
                      ? 'Not set yet — add it so attendance data can be matched'
                      : ''
                }
                inputProps={{ maxLength: 50 }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                  },
                  '& .MuiFormHelperText-root': {
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.8125rem',
                  },
                }}
              />
            );
          })()}

          {(() => {
            const options = users
              .filter((u) => APPROVER_ROLES.includes(u.role) && u.isActive !== false && u.id !== editId)
              .sort(byRoleThenName);
            const value = options.find((o) => o.id === form.reportsTo) || null;
            const isApprover = APPROVER_ROLES.includes(form.role);
            return (
              <Autocomplete
                options={options}
                value={value}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                groupBy={(o) => roleLabel(o.role)}
                onChange={(e, v) => setForm({ ...form, reportsTo: v ? v.id : null })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    label="Reports to"
                    placeholder="Search for a manager..."
                    helperText={isApprover
                      ? 'Project managers and team leaders approve their own timesheets, so this is optional.'
                      : 'The manager who approves this person\'s timesheets.'}
                    sx={{
                      '& .MuiInputBase-input': { fontFamily: '"Inter", sans-serif', fontSize: '0.9375rem' },
                      '& .MuiInputLabel-root': { fontFamily: '"Inter", sans-serif', fontSize: '0.9375rem' },
                      '& .MuiFormHelperText-root': { fontFamily: '"Inter", sans-serif', fontSize: '0.8125rem' },
                    }}
                  />
                )}
              />
            );
          })()}

          {!isFieldHidden('password') && editId && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.changePassword}
                  onChange={handleChange}
                  name="changePassword"
                />
              }
              label="Change Password"
              sx={{ 
                mt: 1, 
                mb: 1,
                '& .MuiFormControlLabel-label': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: 400,
                }
              }}
            />
          )}
          
          {!isFieldHidden('password') && (!editId || form.changePassword) && (
            <TextField
              margin="normal"
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              fullWidth
              required={!editId || form.changePassword}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                },
              }}
            />
          )}
          
          {!isFieldHidden('role') && (
            <FormControl fullWidth margin="normal">
              <InputLabel 
                id="role-label"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                }}
              >
                Role
              </InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={form.role}
                label="Role"
                onChange={handleChange}
                disabled={loadingRoles}
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                  '& .MuiSelect-select': {
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                  }
                }}
              >
                {getAvailableRoles(user.role, roles).map((r) => (
                  <MenuItem 
                    key={r.value || r.id} 
                    value={r.value}
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.9375rem',
                    }}
                  >
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {!isFieldHidden('serviceid') && (() => {
            const isAdminRole = (form.role || '').toLowerCase() === 'admin';
            return (
              <FormControl fullWidth margin="normal" required={!isAdminRole}>
                <InputLabel
                  id="service-label"
                  shrink
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                  }}
                >
                  {isAdminRole ? 'Service (optional — leave blank for global admin)' : 'Service *'}
                </InputLabel>
                <Select
                  labelId="service-label"
                  name="serviceid"
                  value={form.serviceid ?? ''}
                  label={isAdminRole ? 'Service (optional — leave blank for global admin)' : 'Service *'}
                  onChange={handleChange}
                  disabled={loadingServices}
                  required={!isAdminRole}
                  error={!isAdminRole && !form.serviceid}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected === '' || selected == null) {
                      return isAdminRole
                        ? <em>All services (global admin)</em>
                        : <em style={{ color: 'rgba(0,0,0,0.4)' }}>Select a service</em>;
                    }
                    const svc = services.find(s => s.Id === selected || s.Id === parseInt(selected, 10));
                    return svc ? svc.Service : selected;
                  }}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                    '& .MuiSelect-select': {
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.9375rem',
                    }
                  }}
                >
                  {isAdminRole && (
                    <MenuItem
                      value=""
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.9375rem',
                        fontStyle: 'italic',
                      }}
                    >
                      All services (global admin)
                    </MenuItem>
                  )}
                  {services.map((service) => (
                    <MenuItem
                      key={service.Id}
                      value={service.Id}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.9375rem',
                      }}
                    >
                      {service.Service}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : (editId ? 'Save Changes' : 'Add User')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            fontFamily: '"Inter", sans-serif',
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              letterSpacing: '0.01em',
            }}
          >
            Are you sure you want to delete user "{userToDelete?.name}"?
          </DialogContentText>
          {userToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography 
                variant="body2"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  letterSpacing: '0.01em',
                  '& strong': {
                    fontWeight: 600,
                  }
                }}
              >
                <strong>Name:</strong> {userToDelete.name}<br/>
                <strong>Email:</strong> {userToDelete.email}<br/>
                <strong>Role:</strong> {userToDelete.role.replace('_', ' ')}<br/>
                <strong>Status:</strong> {userToDelete.isActive !== false ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change Confirmation Dialog */}
      <Dialog 
        open={emailChangeOpen} 
        onClose={handleEmailChangeCancel}
        PaperProps={{
          sx: {
            fontFamily: '"Inter", sans-serif',
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          Confirm Email Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              letterSpacing: '0.01em',
            }}
          >
            You are about to change the user's email address. This will:
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography 
              variant="body2" 
              color="warning.contrastText"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                letterSpacing: '0.01em',
                '& strong': {
                  fontWeight: 600,
                }
              }}
            >
              <strong>⚠️ Important:</strong><br/>
              • User will need to re-authenticate with Microsoft<br/>
              • All existing sessions will be invalidated<br/>
              • User should be notified of the email change
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography 
              variant="body2"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                letterSpacing: '0.01em',
                '& strong': {
                  fontWeight: 600,
                }
              }}
            >
              <strong>From:</strong> {originalEmail}<br/>
              <strong>To:</strong> {form.email}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEmailChangeCancel}
            disabled={changingEmail}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEmailChangeConfirm}
            color="warning"
            variant="contained"
            disabled={changingEmail}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            {changingEmail ? <CircularProgress size={24} color="inherit" /> : 'Change Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Role Dialog */}
      <Dialog
        open={addRoleDialogOpen}
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseAddRoleDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            fontFamily: '"Inter", sans-serif',
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          Add New Role
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ 
              mt: 2,
              '& .MuiInputBase-input': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
                fontWeight: 400,
                letterSpacing: '0.01em',
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
              },
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel 
              id="new-role-service-label"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
              }}
            >
              Service (Optional)
            </InputLabel>
            <Select
              labelId="new-role-service-label"
              value={newRoleServiceId}
              label="Service (Optional)"
              onChange={(e) => setNewRoleServiceId(e.target.value)}
              disabled={loadingServices}
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
                '& .MuiSelect-select': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                }
              }}
            >
              <MenuItem value="" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.9375rem' }}>
                <em>None (Global Role)</em>
              </MenuItem>
              {services.map((service) => (
                <MenuItem 
                  key={service.Id} 
                  value={service.Id}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                  }}
                >
                  {service.Service}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseAddRoleDialog} 
            disabled={savingRole}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNewRole} 
            variant="contained" 
            disabled={savingRole || !newRoleName.trim()}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            {savingRole ? 'Adding...' : 'Add Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Managers: suggested "Reports to" for everyone without one */}
      <Dialog open={reportsToOpen} onClose={() => !applyingReportsTo && setReportsToOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Assign managers</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: '0.875rem' }}>
            Everyone who logs time and has no "Reports to" yet. The suggestion is the manager of the projects they
            logged the most hours on in the last 90 days, or failing that a project they are on the team of.
            Change any suggestion, untick anyone you are not sure about, then apply.
          </DialogContentText>
          {loadingReportsTo ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : reportsToRows.length === 0 ? (
            <Alert severity="success">Everyone who logs time already has a manager.</Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
                {reportsToRows.filter((r) => r.suggestedId).length} of {reportsToRows.length} have a suggestion.
                {' '}{reportsToRows.filter((r) => !r.suggestedId).length} have no project history and need picking by hand.
              </Alert>
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={reportsToRows.length > 0 && reportsToRows.every((r) => r.include || !r.choice)}
                          onChange={(e) => setReportsToRows((rows) => rows.map((r) => ({ ...r, include: e.target.checked && !!r.choice })))}
                        />
                      </TableCell>
                      <TableCell>Person</TableCell>
                      <TableCell sx={{ minWidth: 260 }}>Reports to</TableCell>
                      <TableCell>Basis</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportsToRows.map((r) => {
                      const mgr = reportsToManagers.find((m) => m.id === r.choice) || null;
                      const crossService = mgr && r.serviceid && mgr.serviceid && mgr.serviceid !== r.serviceid;
                      return (
                        <TableRow key={r.id} sx={{ bgcolor: r.error ? 'rgba(244, 67, 54, 0.08)' : undefined }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={!!r.include} disabled={!r.choice}
                              onChange={(e) => setReportsToRows((rows) => rows.map((x) => (x.id === r.id ? { ...x, include: e.target.checked } : x)))} />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{r.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{roleLabel(r.role)}{r.serviceName ? ` · ${r.serviceName}` : ''}</Typography>
                            {r.error && <Typography variant="caption" display="block" color="error">{r.error}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={reportsToManagers.filter((m) => m.id !== r.id)}
                              value={mgr}
                              getOptionLabel={(o) => o.name || ''}
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              groupBy={(o) => roleLabel(o.role)}
                              onChange={(e, v) => setReportsToRows((rows) => rows.map((x) => (x.id === r.id ? { ...x, choice: v ? v.id : null, include: !!v } : x)))}
                              renderInput={(params) => <TextField {...params} placeholder="Pick a manager" variant="outlined" />}
                            />
                            {crossService && <Typography variant="caption" color="warning.main">Manager is in a different service</Typography>}
                          </TableCell>
                          <TableCell>
                            {r.basis === 'hours' && <Chip size="small" label={`${Math.round((r.share || 0) * 100)}% of hours`} color={r.share >= 0.6 ? 'success' : 'warning'} variant="outlined" />}
                            {r.basis === 'team' && <Chip size="small" label="Project team" variant="outlined" />}
                            {!r.basis && <Chip size="small" label="No history" variant="outlined" sx={{ opacity: 0.6 }} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportsToOpen(false)} disabled={applyingReportsTo}>Close</Button>
          <Button
            variant="contained"
            onClick={applyReportsTo}
            disabled={applyingReportsTo || loadingReportsTo || !reportsToRows.some((r) => r.include && r.choice)}
          >
            {applyingReportsTo ? <CircularProgress size={20} color="inherit" /> : `Apply to ${reportsToRows.filter((r) => r.include && r.choice).length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add many users: type, paste from Excel, or import a file */}
      {bulkGridOpen && (
        <React.Suspense fallback={null}>
        <BulkGrid
          open
          onClose={() => setBulkGridOpen(false)}
          title="Add many users"
          subtitle="Type, paste a block from Excel or Google Sheets, or import a .xlsx / .csv file. Each person gets the usual welcome email."
          columns={bulkColumns}
          validateRow={validateUserRow}
          dedupeKey={userDedupeKey}
          dryRun={bulkDryRun}
          onSubmit={bulkSubmit}
          submitLabel={(n) => `Add ${n} user${n === 1 ? '' : 's'}`}
          templateName="users_template.csv"
        />
        </React.Suspense>
      )}
    </Box>
  );
};

export default UserManagement;
